import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cargoApi, type Warehouse } from '@/api/cargo';
import { PageHead, DataTable, Drawer, SectionCard, Field, StatusBadge, type Col } from '@/shared/ui/kit';
import { useToast } from '@/app/providers/ToastProvider';
import { WAREHOUSE_TYPE, toOptions } from '@/shared/cargo';

const empty = { Code: '', Name: '', Type: 1, Country: '', City: '', Address: '', Phone: '', IsActive: true };

export function WarehousesPage() {
  const toast = useToast();
  const [edit, setEdit] = useState<Warehouse | null | undefined>(undefined);
  const [form, setForm] = useState({ ...empty });
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ['cargo', 'warehouses'], queryFn: cargoApi.warehouses });

  function open(w: Warehouse | null) {
    setEdit(w);
    setForm(w ? { Code: w.Code, Name: w.Name, Type: w.Type, Country: w.Country ?? '', City: w.City ?? '', Address: w.Address ?? '', Phone: w.Phone ?? '', IsActive: w.IsActive } : { ...empty });
  }
  async function save() {
    if (!form.Code.trim() || !form.Name.trim()) return toast('Код, нэр оруулна уу.', 'warning');
    setBusy(true);
    try {
      await cargoApi.saveWarehouse(edit ? edit.Id : null, form);
      toast('Хадгаллаа.', 'success');
      setEdit(undefined);
      q.refetch();
    } catch (e) { toast((e as Error).message, 'danger'); } finally { setBusy(false); }
  }

  const cols: Col<Warehouse>[] = [
    { key: 'Code', label: 'Код', width: 110, render: (r) => <button className="ct-link" onClick={(e) => { e.stopPropagation(); open(r); }}>{r.Code}</button> },
    { key: 'Name', label: 'Нэр', render: (r) => r.Name },
    { key: 'Type', label: 'Төрөл', render: (r) => r.TypeLabel },
    { key: 'City', label: 'Хот', render: (r) => r.City ?? '—' },
    { key: 'IsActive', label: 'Төлөв', render: (r) => <StatusBadge label={r.IsActive ? 'Идэвхтэй' : 'Идэвхгүй'} color={r.IsActive ? 'var(--ts-ok)' : 'var(--ts-muted)'} /> },
  ];

  return (
    <div>
      <PageHead title="Агуулах / Салбар" subtitle="Гадаад, дотоод агуулах болон очиж авах салбарууд."
        actions={<button className="ts-btn ts-btn-primary" onClick={() => open(null)}><i className="bi bi-plus-lg" /> Шинэ</button>} />
      <DataTable cols={cols} rows={q.data ?? []} onRowClick={open} empty="Агуулах алга." />
      {edit !== undefined && (
        <Drawer title={edit ? edit.Name : 'Шинэ агуулах'} open onClose={() => setEdit(undefined)}
          footer={<><button className="ts-btn ts-btn-ghost" onClick={() => setEdit(undefined)}>Болих</button><button className="ts-btn ts-btn-primary" disabled={busy} onClick={save}><i className="bi bi-check2" /> Хадгалах</button></>}>
          <SectionCard icon="bi-building" title="Үндсэн мэдээлэл">
            <div className="ts-row">
              <div style={{ gridColumn: 'span 4' }}><Field label="Код" required><input className="ts-input" required value={form.Code} onChange={(e) => setForm({ ...form, Code: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 8' }}><Field label="Нэр" required><input className="ts-input" required value={form.Name} onChange={(e) => setForm({ ...form, Name: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 4' }}><Field label="Төрөл"><select className="ts-select" value={form.Type} onChange={(e) => setForm({ ...form, Type: Number(e.target.value) })}>{toOptions(WAREHOUSE_TYPE).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Field></div>
              <div style={{ gridColumn: 'span 4' }}><Field label="Улс"><input className="ts-input" value={form.Country} onChange={(e) => setForm({ ...form, Country: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 4' }}><Field label="Хот"><input className="ts-input" value={form.City} onChange={(e) => setForm({ ...form, City: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 8' }}><Field label="Хаяг"><input className="ts-input" value={form.Address} onChange={(e) => setForm({ ...form, Address: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 4' }}><Field label="Утас"><input className="ts-input" value={form.Phone} onChange={(e) => setForm({ ...form, Phone: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 12' }}><label className="ts-flex-row" style={{ cursor: 'pointer' }}><input type="checkbox" checked={form.IsActive} onChange={(e) => setForm({ ...form, IsActive: e.target.checked })} /> Идэвхтэй</label></div>
            </div>
          </SectionCard>
        </Drawer>
      )}
    </div>
  );
}
